import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, Mail, LogIn, UserPlus, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { api, apiError, setUserToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PasswordPolicyChecklist } from "./PasswordPolicyChecklist";
import { RegisterWizard } from "./RegisterWizard";

export const AccountAuth = () => {
  const { t } = useTranslation("auth");
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [company, setCompany] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [code, setCode] = useState("");
  const [flow, setFlow] = useState(null); // null | "forgot" | "reset"
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [fcNew, setFcNew] = useState("");
  const [fcConfirm, setFcConfirm] = useState("");
  const [forcePolicy, setForcePolicy] = useState(null);
  const [pending, setPending] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const goNext = (user) => {
    const next = params.get("next");
    if (next && next.startsWith("/")) navigate(next);
    else if (user?.role === "admin") navigate("/admin");
    else navigate("/compte");
  };

  const finish = (data) => {
    if (data.must_change_password) {
      setUserToken(data.token);
      setPending({ token: data.token, user: data.user });
      setFlow("force");
      toast.info(t("force.mustSet"));
      api.get("/account/password-policy").then((r) => setForcePolicy(r.data.policy)).catch(() => {});
      return;
    }
    if (data.must_enable_2fa) {
      setUserToken(data.token);
      setPending({ token: data.token, user: data.user });
      setFlow("enable2fa");
      toast.info(t("enable2fa.required"));
      api.post("/account/two-factor/request-code").catch(() => {});
      return;
    }
    login(data.token, data.user);
    toast.success(t("login.success"));
    goNext(data.user);
  };

  const enable2faSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error(t("errors.codeLength")); return; }
    setLoading(true);
    try {
      await api.post("/account/two-factor/confirm", { code });
      login(pending.token, { ...pending.user, two_factor_enabled: true });
      toast.success(t("enable2fa.activated"));
      goNext(pending.user);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const resend2fa = async () => {
    try {
      await api.post("/account/two-factor/request-code");
      toast.success(t("enable2fa.resent"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const forceSubmit = async (e) => {
    e.preventDefault();
    if (fcNew.length < 8) { toast.error(t("errors.passwordMin8")); return; }
    if (fcNew !== fcConfirm) { toast.error(t("errors.confirmMismatch")); return; }
    setLoading(true);
    try {
      await api.post("/account/change-password", { current_password: password, new_password: fcNew });
      login(pending.token, pending.user);
      toast.success(t("force.updated"));
      goNext(pending.user);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        const { data } = await api.post("/account/register", {
          company,
          first_name: firstName,
          last_name: lastName,
          email,
          password,
        });
        finish(data);
      } else {
        const { data } = await api.post("/account/login", { email, password });
        if (data.two_factor_required) {
          setOtpStep(true);
          toast.info(t("login.otpSent"));
        } else {
          finish(data);
        }
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/account/verify-otp", { email, code });
      finish(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const forgotRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/account/forgot-password", { email });
      toast.success(t("forgot.genericSent"));
      setFlow("reset");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const resetSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/account/reset-password", {
        email,
        code: resetCode,
        new_password: newPassword,
      });
      toast.success(t("reset.done"));
      setFlow(null);
      setMode("login");
      setResetCode("");
      setNewPassword("");
      setPassword("");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (mode === "register" && !otpStep && !flow) {
    return (
      <div className="flex items-center justify-center py-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="glass rounded-2xl p-8 w-full max-w-lg"
        >
          <RegisterWizard
            prefillPlan={params.get("plan")}
            onDone={finish}
            onSwitchLogin={() => setMode("login")}
          />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center py-8">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="glass rounded-2xl p-8 w-full max-w-md"
      >
        <div className="w-12 h-12 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-5">
          <ShieldCheck className="w-6 h-6 text-emerald-600" />
        </div>

        {flow === "enable2fa" ? (
          <form onSubmit={enable2faSubmit} data-testid="enable-2fa-form">
            <h2 className="font-heading text-2xl font-extrabold tracking-tight mb-1">
              {t("enable2fa.title")}
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              {t("enable2fa.subtitle")}
            </p>
            <Label className="text-slate-600 mb-1.5 block">{t("enable2fa.code")}</Label>
            <Input
              data-testid="enable-2fa-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t("enable2fa.codePlaceholder")}
              maxLength={6}
              className="bg-slate-50 border-slate-200 text-center tracking-widest"
              required
            />
            <Button
              data-testid="enable-2fa-submit"
              type="submit"
              disabled={loading}
              className="w-full mt-5 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              {t("enable2fa.submit")}
            </Button>
            <button type="button" data-testid="enable-2fa-resend" onClick={resend2fa}
              className="w-full mt-3 text-sm text-emerald-600 hover:underline">
              {t("enable2fa.resend")}
            </button>
          </form>
        ) : flow === "force" ? (
          <form onSubmit={forceSubmit} data-testid="force-change-form">
            <h2 className="font-heading text-2xl font-extrabold tracking-tight mb-1">
              {t("force.title")}
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              {t("force.subtitle")}
            </p>
            <Label className="text-slate-600 mb-1.5 block">{t("force.newPassword")}</Label>
            <Input
              data-testid="force-new-password"
              type="password"
              value={fcNew}
              onChange={(e) => setFcNew(e.target.value)}
              placeholder={t("force.newPasswordPlaceholder")}
              className="bg-slate-50 border-slate-200"
              required
            />
            <PasswordPolicyChecklist policy={forcePolicy} value={fcNew} className="mt-3" />
            <Label className="text-slate-600 mb-1.5 block mt-4">{t("force.confirm")}</Label>
            <Input
              data-testid="force-confirm-password"
              type="password"
              value={fcConfirm}
              onChange={(e) => setFcConfirm(e.target.value)}
              placeholder={t("force.confirmPlaceholder")}
              className="bg-slate-50 border-slate-200"
              required
            />
            <Button
              data-testid="force-submit"
              type="submit"
              disabled={loading}
              className="w-full mt-5 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
              {t("force.submit")}
            </Button>
          </form>
        ) : flow === "forgot" ? (
          <form onSubmit={forgotRequest} data-testid="forgot-form">
            <h2 className="font-heading text-2xl font-extrabold tracking-tight mb-1">
              {t("forgot.title")}
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              {t("forgot.subtitle")}
            </p>
            <Label className="text-slate-600 mb-1.5 block">{t("forgot.email")}</Label>
            <Input
              data-testid="forgot-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("forgot.emailPlaceholder")}
              className="bg-slate-50 border-slate-200"
              required
            />
            <Button
              data-testid="forgot-submit"
              type="submit"
              disabled={loading}
              className="w-full mt-5 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              {t("forgot.submit")}
            </Button>
            <div className="text-center text-sm text-slate-500 mt-5">
              <button
                type="button"
                data-testid="forgot-back-login"
                onClick={() => { setFlow(null); setMode("login"); }}
                className="text-emerald-600 hover:text-emerald-600 font-medium"
              >
                {t("common.backToLogin")}
              </button>
            </div>
          </form>
        ) : flow === "reset" ? (
          <form onSubmit={resetSubmit} data-testid="reset-form">
            <h2 className="font-heading text-2xl font-extrabold tracking-tight mb-1">
              {t("reset.title")}
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              {t("reset.subtitle", { email })}
            </p>
            <Label className="text-slate-600 mb-1.5 block">{t("reset.code")}</Label>
            <Input
              data-testid="reset-code-input"
              value={resetCode}
              onChange={(e) => setResetCode(e.target.value)}
              placeholder="000000"
              maxLength={6}
              className="bg-slate-50 border-slate-200 font-mono-token text-center text-xl tracking-[0.4em]"
              required
            />
            <Label className="text-slate-600 mb-1.5 block mt-4">{t("reset.newPassword")}</Label>
            <Input
              data-testid="reset-password-input"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t("reset.newPasswordPlaceholder")}
              className="bg-slate-50 border-slate-200"
              required
            />
            <Button
              data-testid="reset-submit"
              type="submit"
              disabled={loading}
              className="w-full mt-5 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
              {t("reset.submit")}
            </Button>
            <div className="text-center text-sm text-slate-500 mt-5">
              <button
                type="button"
                data-testid="reset-back-login"
                onClick={() => { setFlow(null); setMode("login"); }}
                className="text-emerald-600 hover:text-emerald-600 font-medium"
              >
                {t("common.backToLogin")}
              </button>
            </div>
          </form>
        ) : otpStep ? (
          <form onSubmit={verifyOtp} data-testid="otp-form">
            <h2 className="font-heading text-2xl font-extrabold tracking-tight mb-1">
              {t("otp.title")}
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              {t("otp.subtitle", { email })}
            </p>
            <Label className="text-slate-600 mb-1.5 block">{t("otp.codeLogin")}</Label>
            <Input
              data-testid="otp-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="000000"
              maxLength={6}
              className="bg-slate-50 border-slate-200 font-mono-token text-center text-xl tracking-[0.4em]"
              required
            />
            <Button
              data-testid="otp-submit"
              type="submit"
              disabled={loading}
              className="w-full mt-5 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
              {t("otp.verify")}
            </Button>
          </form>
        ) : (
          <form onSubmit={submit} data-testid="account-auth-form">
            <h2 className="font-heading text-2xl font-extrabold tracking-tight mb-1">
              {mode === "register" ? t("register.title") : t("login.title")}
            </h2>
            <p className="text-sm text-slate-500 mb-6">
              {mode === "register" ? t("register.subtitle") : t("login.subtitle")}
            </p>

            <div className="space-y-4">
              {mode === "register" && (
                <>
                  <div>
                    <Label className="text-slate-600 mb-1.5 block">{t("register.company")}</Label>
                    <Input
                      data-testid="auth-company"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      placeholder={t("register.companyPlaceholder")}
                      className="bg-slate-50 border-slate-200"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-600 mb-1.5 block">{t("register.firstName")}</Label>
                      <Input
                        data-testid="auth-first-name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder={t("register.firstNamePlaceholder")}
                        className="bg-slate-50 border-slate-200"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-slate-600 mb-1.5 block">{t("register.lastName")}</Label>
                      <Input
                        data-testid="auth-last-name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder={t("register.lastNamePlaceholder")}
                        className="bg-slate-50 border-slate-200"
                        required
                      />
                    </div>
                  </div>
                </>
              )}
              <div>
                <Label className="text-slate-600 mb-1.5 block">{t("login.email")}</Label>
                <Input
                  data-testid="auth-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("login.emailPlaceholder")}
                  className="bg-slate-50 border-slate-200"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-600 mb-1.5 block">{t("login.password")}</Label>
                <Input
                  data-testid="auth-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("login.passwordPlaceholder")}
                  className="bg-slate-50 border-slate-200"
                  required
                />
              </div>

              {mode === "login" && (
                <div className="text-right -mt-2">
                  <button
                    type="button"
                    data-testid="forgot-password-link"
                    onClick={() => setFlow("forgot")}
                    className="text-sm text-emerald-600 hover:text-emerald-600 font-medium"
                  >
                    {t("login.forgot")}
                  </button>
                </div>
              )}

              <Button
                data-testid="auth-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : mode === "register" ? (
                  <UserPlus className="w-4 h-4 mr-2" />
                ) : (
                  <LogIn className="w-4 h-4 mr-2" />
                )}
                {mode === "register" ? t("register.submit") : t("login.submit")}
              </Button>
            </div>

            <div className="text-center text-sm text-slate-500 mt-5">
              {mode === "register" ? t("login.haveAccount") + " " : t("login.noAccount") + " "}
              <button
                type="button"
                data-testid="auth-toggle-mode"
                onClick={() => setMode(mode === "register" ? "login" : "register")}
                className="text-emerald-600 hover:text-emerald-600 font-medium"
              >
                {mode === "register" ? t("login.signin") : t("login.createAccount")}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};
